import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getPlaceholderForPFTrans,
  getPlaceholderForPFFinal,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  getStatusForPFTrans,
  getStatusForPFFinal,
  isFromScratch,
  statusKeys
} from '../../../data';
import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { jurisdictionService } from '../../../../../../services/jurisdiction.service';
import { useAutomateReportGeneration } from '../../../../../../services/automation.service';
import { CloseIcon } from '../../../../../DataExtraction/components/FundMappingModal/icons';
import toast from 'react-hot-toast';
import { FaArrowRight } from 'react-icons/fa6';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { useConvertReport } from '../../../../../../services/period.service';
import { TbTransform } from 'react-icons/tb';
import { extractApiError } from '../../../../../../utils';
import { useValidateDetailedOutputStats } from '../../../../../../services/funds.service';

export const ReportColumn = ({ period, workflowId, refetch, isLoadingPeriods, reportType }) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [allowToShowLoadingPeriods, setAllowToShowLoadingPeriods] = useState(false);

  const isFormPF = reportType === REPORT_TYPES.FORM_PF;

  const status = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getStatusForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getStatusForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) {
      const _status = getStatusForCustomReportType(period);
      return _status;
    }

    return getStatus(period);
  }, [reportType, period]);

  const isScratch = isFromScratch(period);
  const placeholder = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getPlaceholderForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getPlaceholderForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getPlaceholderForCustomReportType(period);

    return getPlaceholder(period);
  }, [reportType, period]);

  const generateReport = useAutomateReportGeneration();
  const convertReport = useConvertReport();
  const validateDetailedOutputStats = useValidateDetailedOutputStats();

  useEffect(() => {
    if (!isLoadingPeriods) setAllowToShowLoadingPeriods(false);
  }, [isLoadingPeriods]);

  const getJurisdictionsList = async () => {
    if (reportType === REPORT_TYPES.CUSTOM) {
      navigate(`/output-tables/${workflowId}/${period?.id}`);

      return;
    }

    setIsLoading(true);

    jurisdictionService
      .getJurisdictions(period?.id)
      .then(res => {
        const el = res?.[0];
        const natCode = el?.reporting_member_state;
        const isFCA = natCode === 'UK' || natCode === 'uk';

        if (isFormPF) {
          navigate(
            `/report/pf/us/${workflowId}/${period.id}${period.is_grouped ? '?reportType=grouped' : ''}`
          );
          return;
        }

        if (isFCA)
          navigate(
            `/report/fca/uk/${workflowId}/${period.id}${period.is_grouped ? '?reportType=grouped' : ''}`
          );
        else
          navigate(
            `/report/esma/${natCode?.toLowerCase()}/${workflowId}/${period.id}${period.is_grouped ? '?reportType=grouped' : ''}`
          );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClick = () => {
    getJurisdictionsList();
  };

  const onGenerateReport = () => {
    generateReport.mutate(
      {
        period_id: period?.id
      },
      {
        onSuccess: () => {
          refetch();
          setAllowToShowLoadingPeriods(true);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to generate this report.'));
        }
      }
    );
  };

  const onValidateAndGenerateReport = () => {
    validateDetailedOutputStats.mutate(
      {
        periodId: period?.id
      },
      {
        onSuccess: stats => {
          const hasBlockingIssue = stats?.errors?.length > 0;

          if (hasBlockingIssue) {
            toast.error(
              'Your recent changes caused error in output table, please fix it before generating the report.'
            );

            return;
          }

          onGenerateReport();
        },
        onError: err => {
          toast.error(
            extractApiError(err, 'Unable to validate output data before generating the report.')
          );
        }
      }
    );
  };

  const onConvertReport = () => {
    convertReport.mutate(
      {
        periodId: period?.id,
        workflowId: workflowId
      },
      {
        onSuccess: () => {
          refetch();
          setAllowToShowLoadingPeriods(true);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to convert this report.'));
        }
      }
    );
  };

  const onOpenDrawer = () => {
    if (isScratch) onClick();
    else onValidateAndGenerateReport();
  };

  if (isLoading) return <ActionLabel placeholder="Redirecting..." />;

  if (validateDetailedOutputStats.isLoading)
    return <ActionLabel placeholder="Checking output..." />;

  if (generateReport.isLoading) return <ActionLabel placeholder="Report generating..." />;

  if (reportType === REPORT_TYPES.CUSTOM) {
    if (
      status === statusKeys.FILES ||
      status === statusKeys.EXTRACTION ||
      status === statusKeys.TRANSFORMATION
    )
      return <ActionLabel placeholder={placeholder} />;

    return (
      <ActionLabel
        label="Check Output Data"
        color="#40C057"
        onClick={getJurisdictionsList}
        rightIcon={<FaArrowRight />}
      />
    );
  }

  if (reportType === REPORT_TYPES.CONVERSION) {
    if (
      status !== statusKeys.APPROVAL &&
      status !== statusKeys.SUBMISSION &&
      status !== statusKeys.REPORT &&
      status !== statusKeys.END
    )
      return <ActionLabel placeholder={placeholder} />;

    if (period?.report_progress_status === 'Completed')
      return (
        <ActionLabel
          label="View Report"
          color="#40C057"
          onClick={getJurisdictionsList}
          rightIcon={<FaArrowRight />}
        />
      );

    if ((period?.report_task?.status === 'In progress' && !isScratch) || convertReport.isLoading)
      return <ActionLabel label="Generating report..." />;

    if (period?.report_task?.status === 'Failed' && !isScratch)
      return (
        <ActionLabel
          label="Failed"
          color="#E03131"
          rightIcon={<CloseIcon size={16} color="#E03131" />}
          onClick={onValidateAndGenerateReport}
        />
      );

    return (
      <ActionLabel
        noFill
        label="Convert & Generate"
        leftIcon={<TbTransform color="#228BE6" size={16} />}
        onClick={onConvertReport}
      />
    );
  }

  if (period?.report_progress_status === 'Completed')
    return (
      <ActionLabel
        label="Report Generated"
        color="#40C057"
        onClick={getJurisdictionsList}
        rightIcon={<FaArrowRight />}
      />
    );

  if (period?.report_task?.status === 'In progress' && !isScratch)
    return <ActionLabel label="Generating report..." />;

  if (period?.report_task?.status === 'Failed' && !isScratch)
    return (
      <ActionLabel
        label="Failed"
        color="#E03131"
        rightIcon={<CloseIcon size={16} color="#E03131" />}
        onClick={onValidateAndGenerateReport}
      />
    );

  if (status === statusKeys.REPORT && period?.report_progress_status === 'Not started')
    return (
      <ActionLabel
        label="Report generate"
        rightIcon={<OutlinedNavigateIcon />}
        onClick={onOpenDrawer}
      />
    );

  if (
    status !== statusKeys.APPROVAL &&
    status !== statusKeys.SUBMISSION &&
    status !== statusKeys.REPORT &&
    status !== statusKeys.END
  )
    return <ActionLabel placeholder={placeholder} />;

  return (
    <ActionLabel
      label="Report Generated"
      color="#40C057"
      onClick={getJurisdictionsList}
      rightIcon={<FaArrowRight />}
    />
  );
};
